import GenericProductService from './GenericProductService';
import StandardProductService from './StandardProductService';

/**
 * Serviço unificado para gerenciamento de produtos
 * Coordena operações entre produtos genéricos e padrão
 */
class ProductManagerService {
    constructor() {
        this.genericService = GenericProductService;
        this.standardService = StandardProductService;
    }

    /**
     * Adiciona um produto ao carrinho (genérico ou padrão)
     * @param {Object} product - Produto a ser adicionado
     * @param {string} saleId - ID da venda
     * @param {number} quantity - Quantidade
     * @returns {Promise<Object>} Resposta da API
     */
    async addProductToCart(product, saleId, quantity = 1) {
        if (this.genericService.isGenericProduct(product)) {
            return await this.addGenericProduct(product, saleId, quantity);
        } else {
            return await this.addStandardProduct(product, saleId, quantity);
        }
    }

    /**
     * Adiciona um produto genérico ao carrinho
     * @param {Object} product - Produto genérico
     * @param {string} saleId - ID da venda
     * @param {number} quantity - Quantidade
     * @returns {Promise<Object>} Resposta da API
     */
    async addGenericProduct(product, saleId, quantity) {    
        
        const validation = this.genericService.validateGenericProduct(product);
        if (!validation.isValid) {
            console.error('Validação falhou:', validation.errors);
            throw new Error(`Produto genérico inválido: ${validation.errors.join(', ')}`);
        }
        
        const formattedProduct = this.genericService.createGenericProduct({
            ...product,
           
        });
        
          

        return await this.genericService.addItem(formattedProduct, saleId);
    }

    /**
     * Adiciona um produto padrão ao carrinho
     * @param {Object} product - Produto padrão
     * @param {string} saleId - ID da venda
     * @param {number} quantity - Quantidade
     * @returns {Promise<Object>} Resposta da API
     */
    async addStandardProduct(product, saleId, quantity) {
        
        // Verificar estoque para produtos padrão
        const stockCheck = await this.standardService.checkStock(product.id || product.product_id);
        if (!stockCheck.available) {
            throw new Error(`Estoque insuficiente. Disponível: ${stockCheck.currentStock}, Solicitado: ${stockCheck.requestedQuantity}`);
        }

        
        const formattedProduct = this.standardService.formatProductForCart(product, quantity);
        return await this.standardService.addItem(formattedProduct, saleId);
    }

    /**
     * Atualiza um produto no carrinho
     * @param {Object} item - Item a ser atualizado
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async updateProductInCart(item, saleId) {
        if (this.genericService.isGenericProduct(item)) {
            return await this.genericService.updateItem(item, saleId);
        } else {
            return await this.standardService.addItem(item, saleId);
        }
    }

    /**
     * Incrementa a quantidade de um produto
     * @param {Object} item - Item a ser incrementado
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async incrementProduct(item, saleId) {        
        if (this.genericService.isGenericProduct(item)) {
            return await this.genericService.incrementItem(saleId, {
                product_id: item.product_id,
                product_name: item.product_name
            });
        } else {
            return await this.standardService.incrementItem(saleId, item.product_id);
        }
    }

    /**
     * Decrementa a quantidade de um produto
     * @param {Object} item - Item a ser decrementado
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async decrementProduct(item, saleId) {
        if (this.genericService.isGenericProduct(item)) {
            return await this.genericService.decrementItem(saleId, {
                product_id: item.product_id,
                product_name: item.product_name
            });
        } else {
            return await this.standardService.decrementItem(saleId, item.product_id);
        }
    }

    /**
     * Remove um produto do carrinho
     * @param {Object} item - Item a ser removido
     * @param {string} saleId - ID da venda
     * @returns {Promise<Object>} Resposta da API
     */
    async removeProductFromCart(item, saleId) {
        
        if (this.genericService.isGenericProduct(item)) {
            return await this.genericService.deleteItem(saleId, {
                product_id: item.product_id,
                product_name: item.product_name
            });
        } else {
            return await this.standardService.deleteItem(saleId, item.product_id);
        }
    }

    /**
     * Verifica se um produto já existe no carrinho
     * @param {Array} cart - Carrinho de compras
     * @param {Object} product - Produto a ser verificado
     * @returns {Object|null} Item existente ou null
     */
    findExistingProduct(cart, product) {
        if (this.genericService.isGenericProduct(product)) {
            return this.genericService.findExistingGenericItem(cart, product.product_name);
        } else {
            return this.standardService.findExistingStandardItem(cart, product.product_id);
        }
    }

    /**
     * Verifica se um produto genérico deve ser atualizado ou adicionado como novo
     * @param {Array} cart - Carrinho de compras
     * @param {Object} product - Produto a ser verificado
     * @returns {Object} { shouldUpdate: boolean, existingItem: Object|null }
     */
    shouldUpdateProduct(cart, product) {
        
        if (this.genericService.isGenericProduct(product)) {
            return this.genericService.shouldUpdateGenericProduct(cart, product);
        } else {
            
            const existingItem = this.standardService.findExistingStandardItem(cart, product.id);
            return {
                shouldUpdate: !!existingItem,
                existingItem: existingItem
            };
        }
    }

    /**
     * Atualiza um item existente no carrinho
     * @param {Object} existingItem - Item existente
     * @param {Object} newProduct - Novo produto
     * @param {number} quantity - Quantidade a adicionar
     * @returns {Object} Item atualizado
     */
    updateExistingItem(existingItem, newProduct, quantity) {
        if (this.genericService.isGenericProduct(existingItem)) {
            return this.genericService.updateExistingItem(existingItem, newProduct, quantity);
        } else {
            return this.standardService.updateExistingItem(existingItem, newProduct, quantity);
        }
    }

    /**
     * Verifica se um produto é genérico
     * @param {Object} product - Produto a ser verificado
     * @returns {boolean} True se for genérico
     */
    isGenericProduct(product) {
        return this.genericService.isGenericProduct(product);
    }

    /**
     * Verifica se um produto é padrão
     * @param {Object} product - Produto a ser verificado
     * @returns {boolean} True se for padrão
     */
    isStandardProduct(product) {
        return this.standardService.isStandardProduct(product);
    }

    /**
     * Valida um produto antes de adicionar ao carrinho
     * @param {Object} product - Produto a ser validado
     * @returns {Object} Resultado da validação
     */
    validateProduct(product) {
        if (this.genericService.isGenericProduct(product)) {
            return this.genericService.validateGenericProduct(product);
        } else {
            return this.standardService.validateStandardProduct(product);
        }
    }
}

export default new ProductManagerService();
